// src/pages/CRM/SFA/OpportunityList.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, IconButton, Chip,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Menu, Tooltip, CircularProgress, Alert, Stack, Avatar, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewKanban as KanbanIcon,
  Download as ExportIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { opportunityService, territoryService } from './sfaService';
import {
  OpportunityStage, OpportunityStageLabels, OpportunityStageColors,
  OpportunityTypeLabels, ForecastCategoryLabels, ViewFilterOptions,
  formatCurrency, formatPercent, AllStages, calculateDaysInStage, getStagnantStatus
} from './sfaEnums';

const OpportunityList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [opportunities, setOpportunities] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    per_page: 20,
    total: 0,
    pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    stage: '',
    is_closed: '',
    forecast_category: '',
    close_date_from: null,
    close_date_to: null,
    view: 'all',
    territory_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState([]);
  const [territories, setTerritories] = useState([]);

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedOpp, setSelectedOpp] = useState(null);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, opportunity: null });

  useEffect(() => {
    document.title = 'Opportunities | Sales Force Automation';
    checkPermissions();
    loadTerritories();
  }, []);

  const loadTerritories = async () => {
    try {
      const response = await territoryService.getAll({ is_active: true });
      if (response.success) {
        setTerritories(response.data);
      }
    } catch (err) {
      console.error('Failed to load territories:', err);
    }
  };

  useEffect(() => {
    if (canView) {
      loadOpportunities();
    }
  }, [canView, chatbotId, pagination.page, pagination.per_page, searchTerm, filters]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadOpportunities = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        per_page: pagination.per_page,
        search: searchTerm
      };

      if (chatbotId) params.chatbot_id = chatbotId;
      if (filters.stage) params.stage = filters.stage;
      if (filters.is_closed) params.is_closed = filters.is_closed;
      if (filters.forecast_category) params.forecast_category = filters.forecast_category;
      if (filters.close_date_from) {
        params.close_date_from = filters.close_date_from.toISOString().split('T')[0];
      }
      if (filters.close_date_to) {
        params.close_date_to = filters.close_date_to.toISOString().split('T')[0];
      }
      if (filters.view) params.view = filters.view;
      if (filters.territory_id) params.territory_id = filters.territory_id;

      const response = await opportunityService.getAll(params);
      if (response.success) {
        setOpportunities(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          pages: response.pagination.pages
        }));
      }
    } catch (err) {
      enqueueSnackbar('Failed to load opportunities', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination(prev => ({
      ...prev,
      per_page: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({
      stage: '',
      is_closed: '',
      forecast_category: '',
      close_date_from: null,
      close_date_to: null,
      view: 'all',
      territory_id: ''
    });
    setSearchTerm('');
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(opportunities.map(o => o.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleMenuOpen = (event, opp) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedOpp(opp);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedOpp(null);
  };

  const handleDelete = async () => {
    if (!deleteDialog.opportunity) return;

    try {
      await opportunityService.delete(deleteDialog.opportunity.id);
      enqueueSnackbar('Opportunity deleted', { variant: 'success' });
      setDeleteDialog({ open: false, opportunity: null });
      loadOpportunities();
    } catch (err) {
      enqueueSnackbar('Failed to delete opportunity', { variant: 'error' });
    }
  };

  const getStageChip = (stage) => {
    return (
      <Chip
        label={OpportunityStageLabels[stage] || stage}
        size="small"
        sx={{
          bgcolor: OpportunityStageColors[stage],
          color: 'white',
          fontWeight: 500
        }}
      />
    );
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        You don't have permission to view opportunities.
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Opportunities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your sales pipeline and deals
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Pipeline View">
              <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities/pipeline`)}>
                <KanbanIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Lost Reason Analytics">
              <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/analytics/lost-reasons`)}>
                <AnalyticsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={loadOpportunities}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {canModify && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities/new`)}
              >
                New Opportunity
              </Button>
            )}
          </Box>
        </Box>

        {/* Search & Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search opportunities..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>View</InputLabel>
                <Select
                  value={filters.view}
                  label="View"
                  onChange={(e) => handleFilterChange('view', e.target.value)}
                >
                  {ViewFilterOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Territory</InputLabel>
                <Select
                  value={filters.territory_id}
                  label="Territory"
                  onChange={(e) => handleFilterChange('territory_id', e.target.value)}
                >
                  <MenuItem value="">All Territories</MenuItem>
                  {territories.map(territory => (
                    <MenuItem key={territory.id} value={territory.id}>
                      {territory.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Stage</InputLabel>
                <Select
                  value={filters.stage}
                  label="Stage"
                  onChange={(e) => handleFilterChange('stage', e.target.value)}
                >
                  <MenuItem value="">All Stages</MenuItem>
                  {AllStages.map(stage => (
                    <MenuItem key={stage} value={stage}>
                      {OpportunityStageLabels[stage]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.is_closed}
                  label="Status"
                  onChange={(e) => handleFilterChange('is_closed', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="false">Open</MenuItem>
                  <MenuItem value="true">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <DatePicker
                label="Close From"
                value={filters.close_date_from}
                onChange={(date) => handleFilterChange('close_date_from', date)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <DatePicker
                label="Close To"
                value={filters.close_date_to}
                onChange={(date) => handleFilterChange('close_date_to', date)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </Grid>
          </Grid>
          {(searchTerm || filters.stage || filters.is_closed || filters.close_date_from || filters.close_date_to || filters.view !== 'all' || filters.territory_id) && (
            <Box sx={{ mt: 2 }}>
              <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
                Clear Filters
              </Button>
            </Box>
          )}
        </Paper>

        {/* Table */}
        <Paper>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.length === opportunities.length && opportunities.length > 0}
                      indeterminate={selected.length > 0 && selected.length < opportunities.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Opportunity</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Probability</TableCell>
                  <TableCell>Close Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : opportunities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">No opportunities found</Typography>
                      {canModify && (
                        <Button
                          sx={{ mt: 2 }}
                          startIcon={<AddIcon />}
                          onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities/new`)}
                        >
                          Create First Opportunity
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  opportunities.map((opp) => (
                    <TableRow
                      key={opp.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities/${opp.id}`)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.includes(opp.id)}
                          onChange={() => handleSelect(opp.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {opp.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {opp.opportunity_number}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {opp.account ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.light' }}>
                              <BusinessIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                            <Typography variant="body2">{opp.account.name}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStageChip(opp.stage)}
                          {(() => {
                            const daysInStage = calculateDaysInStage(opp.stage_history, opp.stage);
                            const stagnantStatus = getStagnantStatus(daysInStage);
                            if (stagnantStatus && !opp.is_closed) {
                              return (
                                <Tooltip title={stagnantStatus.message}>
                                  <WarningIcon
                                    fontSize="small"
                                    color={stagnantStatus.color}
                                  />
                                </Tooltip>
                              );
                            }
                            return null;
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(opp.amount, opp.currency)}
                        </Typography>
                        {opp.weighted_amount && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Weighted: {formatCurrency(opp.weighted_amount, opp.currency)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={formatPercent(opp.probability)}
                          size="small"
                          color={
                            opp.probability >= 75 ? 'success' :
                            opp.probability >= 50 ? 'warning' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {opp.close_date ? (
                          <Typography variant="body2">
                            {new Date(opp.close_date).toLocaleDateString()}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, opp)}>
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={handleChangePage}
            rowsPerPage={pagination.per_page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            navigate(`/chatbot/${chatbotId}/crm/opportunities/${selectedOpp?.id}`);
            handleMenuClose();
          }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            View/Edit
          </MenuItem>
          {canModify && (
            <MenuItem
              onClick={() => {
                setDeleteDialog({ open: true, opportunity: selectedOpp });
                handleMenuClose();
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          )}
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, opportunity: null })}>
          <DialogTitle>Delete Opportunity</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{deleteDialog.opportunity?.name}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, opportunity: null })}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default OpportunityList;